import React, { useEffect, useState } from "react";
import { FiFileText } from "react-icons/fi";
import axios from "../../Utils/axiosInstance";
import toast from "react-hot-toast";
import { assets } from './../../Assets/assets';
import { useLocation } from "react-router-dom";
import AppLoader from "../../Components/Common/AppLoader";
import Pagination from "../../Components/Common/Pagination";
import DeclarationDetailCard from "../../Components/UserSideComponents/Declarations/DeclarationDetailCard";
import DeclarationListItem from "../../Components/UserSideComponents/Declarations/DeclarationListItem";

// ─── Skeleton ─────────────────────────────────────────
const DeclarationSkeleton = () => (
  <div className="p-4 rounded-lg border border-gray-200 animate-pulse">
    <div className="h-4 w-32 bg-gray-200 rounded mb-3" />
    <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-20 bg-gray-200 rounded" />
  </div>
);

// ─── Empty State ──────────────────────────────────────
const EmptyDeclarations = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <img
      src={assets.noordervector}
      alt="No Declarations"
      className="w-64 md:w-80 mb-5 opacity-90"
    />
    <h2 className="text-xl font-semibold text-gray-600">
      No Declarations Found
    </h2>
    <p className="text-sm text-gray-400 mt-1">
      You haven't created any declarations yet.
    </p>
  </div>
);

// ─── Error State ──────────────────────────────────────
const ErrorState = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <p className="text-red-500 font-medium mb-3">
      Failed to load declarations
    </p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-primaryColor text-white rounded-lg text-sm"
    >
      Retry
    </button>
  </div>
);

// ─── Main Component ───────────────────────────────────
const Declarations = () => {
  const location = useLocation();
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedDeclaration, setSelectedDeclaration] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [filterReason, setFilterReason] = useState("all");

  const fetchDeclarations = async () => {
    try {
      setLoading(true);
      setError(false);

      const { data } = await axios.get(`/declaration?page=${page}&limit=${limit}`);
      const declarationsList = data.data || [];
      setDeclarations(declarationsList);
      setPagination(data.pagination || null);

      // Auto-select first declaration on desktop
      if (declarationsList.length > 0 && window.innerWidth >= 768 && !selectedDeclaration) {
        setSelectedDeclaration(declarationsList[0]);
      }
    } catch (err) {
      setError(true);
      toast.error("Failed to fetch declarations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get("highlight");

    if (highlightId && declarations.length) {
      const foundDeclaration = declarations.find((d) => d._id === highlightId);

      if (foundDeclaration) {
        setSelectedDeclaration(foundDeclaration);
      }
    }
  }, [location.search, declarations]);

  useEffect(() => {
    fetchDeclarations();
  }, [page, limit]);

  const filteredDeclarations = declarations.filter(declaration => {
    const searchLower = searchQuery.toLowerCase();
    const reasonMatch = filterReason === "all" || declaration.reason === filterReason;

    return reasonMatch && (
      declaration.reason?.toLowerCase().includes(searchLower) ||
      declaration._id?.toLowerCase().includes(searchLower) ||
      declaration.declaration_is_for?.toLowerCase().includes(searchLower)
    );
  });

  // Get unique reasons for filter dropdown
  const uniqueReasons = [...new Set(declarations.map(d => d.reason))].filter(Boolean);

  const handleDeclarationSelect = (declaration) => {
    setSelectedDeclaration(declaration);
    // Scroll to top on mobile
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="2xl:max-w-7xl mx-auto px-4 md:px-8 py-4 sm:py-6 mb-16 md:mb-0">

        {/* Header - Only on mobile when no declaration selected, always on desktop */}
        <div className={`mb-4 sm:mb-5 ${selectedDeclaration && window.innerWidth < 768 ? 'hidden' : 'block'}`}>
          <h1 className="text-xl flex items-center gap-2 sm:text-2xl font-bold text-primaryColor mb-1">
            <FiFileText />My Declarations
          </h1>
          <p className="text-gray-500 text-sm">
            View your declared agreements and legal documents.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <AppLoader message="Loading Your Declarations..." />
        )}

        {/* Error State */}
        {!loading && error && <ErrorState onRetry={fetchDeclarations} />}

        {/* Empty State */}
        {!loading && !error && declarations.length === 0 && <EmptyDeclarations />}

        {/* Declarations Layout */}
        {!loading && !error && declarations.length > 0 && (
          <>
            {/* Mobile View - List or Detail */}
            <div className="md:hidden">
              {!selectedDeclaration ? (
                <>
                  {/* Search and Filter */}
                  <div className="mb-4 space-y-3">
                    {/* Search */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search declaration ID or reason"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      />
                      <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>

                    {/* Filter by Reason */}
                    {uniqueReasons.length > 0 && (
                      <select
                        value={filterReason}
                        onChange={(e) => setFilterReason(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      >
                        <option value="all">All Reasons</option>
                        {uniqueReasons.map((reason) => (
                          <option key={reason} value={reason}>{reason}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Declarations List */}
                  <div className="space-y-3 mb-4">
                    {filteredDeclarations.map((declaration) => (
                      <DeclarationListItem
                        key={declaration._id}
                        declaration={declaration}
                        isActive={false}
                        onClick={() => handleDeclarationSelect(declaration)}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination && (
                    <Pagination
                      pagination={pagination}
                      page={page}
                      setPage={setPage}
                      limit={limit}
                      setLimit={setLimit}
                    />
                  )}
                </>
              ) : (
                <>
                  {/* Back Button */}
                  <button
                    onClick={() => setSelectedDeclaration(null)}
                    className="flex items-center gap-2 text-primaryColor mb-4 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Declarations
                  </button>

                  {/* Declaration Detail */}
                  <DeclarationDetailCard declaration={selectedDeclaration} />
                </>
              )}
            </div>

            {/* Desktop View - Split Layout */}
            <div className="hidden md:block">
              <div className="grid md:grid-cols-12 gap-5">
                {/* Left Sidebar - Declarations List */}
                <div className="md:col-span-4 lg:col-span-3 space-y-4 sticky top-16 h-[calc(100vh-4rem)] overflow-hidden">
                  <div className="h-full overflow-y-scroll space-y-3">
                    {/* Search */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search declaration ID"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      />
                      <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>

                    {/* Filter by Reason */}
                    {uniqueReasons.length > 0 && (
                      <select
                        value={filterReason}
                        onChange={(e) => setFilterReason(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      >
                        <option value="all">All Reasons</option>
                        {uniqueReasons.map((reason) => (
                          <option key={reason} value={reason}>{reason}</option>
                        ))}
                      </select>
                    )}

                    {/* Declarations List */}
                    <div className="space-y-3">
                      {filteredDeclarations.map((declaration) => (
                        <DeclarationListItem
                          key={declaration._id}
                          declaration={declaration}
                          isActive={selectedDeclaration?._id === declaration._id}
                          onClick={() => setSelectedDeclaration(declaration)}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {pagination && pagination.totalPages > 1 && (
                      <div className="pt-3 border-t border-gray-200">
                        <Pagination
                          pagination={pagination}
                          page={page}
                          setPage={setPage}
                          limit={limit}
                          setLimit={setLimit}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Panel - Declaration Detail */}
                <div className="md:col-span-8 lg:col-span-9 bg-white rounded-lg border border-gray-200 p-5">
                  <DeclarationDetailCard declaration={selectedDeclaration} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Declarations;